import type { iWACallLog } from "@sdk/wa-call-logs/wa-call-logs-model";
import { LoadingIndicatorWithoutSpin } from "components/common/loading-indicator/loading-indicator";
import {
  AudioNotificationsService,
  AvailableAudios,
} from "components/common/notifications/audio-notifications-controller";
import {
  isWACallLogActive,
  isWACallLogIncomingRinging,
} from "components/modules/conversations/components/messages-list/components/wa-cloud-calling/call-status";
import {
  WhatsappIncomingCallContainer,
  WhatsappOutgoingCallContainer,
} from "components/modules/conversations/components/messages-list/components/wa-cloud-calling/whatsapp-call-attachment";
import {
  INITATE_WA_CALL_EVENT,
  type InitiateWACallPayload,
} from "components/modules/conversations/components/messages-list/components/wa-cloud-calling/whatsapp-call-attachment-readonly";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { DraggableData, DraggableEvent } from "react-draggable";
import Draggable from "react-draggable";
import { useSelector } from "react-redux";
import { useMediaQuery } from "react-responsive";
import { useHistory } from "react-router-dom";
import { selectCurrentUserId } from "store/modules/users/users.selectors";
import {
  loadWACallLogById,
  loadWACallLogOfRingAgent,
} from "store/modules/wa-call-logs/wa-call-logs.helpers";
import {
  selectWACallLogById,
  selectWACallLogsByRingAgentId,
} from "store/modules/wa-call-logs/wa-call-logs.selectors";
import { useQueryWithStore } from "store/store.hooks";
import { GlobalEventEmitter } from "utils/global-event-emitter";

const POSITION_STORAGE_KEY = "floating-wa-call-position";
const MINIMIZED_STORAGE_KEY = "floating-wa-call-minimized";

function getStoredPosition(): { x: number; y: number } {
  try {
    const s = sessionStorage.getItem(POSITION_STORAGE_KEY);
    if (s) {
      const p = JSON.parse(s) as { x: number; y: number };
      if (typeof p.x === "number" && typeof p.y === "number") return p;
    }
  } catch {
    // ignore
  }
  return { x: 0, y: 0 };
}

function getStoredMinimized(): boolean {
  try {
    const s = sessionStorage.getItem(MINIMIZED_STORAGE_KEY);
    return s === "true";
  } catch {
    return false;
  }
}

const OUTGOING_ACTIVE_STATUSES = [
  "OUTGOING_INIT",
  "OUTGOING_INIT_SUCCESS",
  "RINGING",
] as const;

function isOutgoingActive(callLog: iWACallLog): boolean {
  return (OUTGOING_ACTIVE_STATUSES as readonly string[]).includes(
    callLog.callStatus,
  );
}

function isIncomingCall(callLog: iWACallLog): boolean {
  return (
    callLog.callStatus === "INCOMING" ||
    (callLog.callStatus === "IN_PROGRESS" &&
      callLog.startedSource === "CUSTOMER")
  );
}

function isOutgoingCall(callLog: iWACallLog): boolean {
  return (
    isOutgoingActive(callLog) ||
    (callLog.callStatus === "IN_PROGRESS" && callLog.startedSource === "USER")
  );
}

function getCallStateLabel(callLog: iWACallLog): string {
  if (callLog.callStatus === "IN_PROGRESS") return "In progress";
  if (callLog.callStatus === "INCOMING") return "Incoming";
  if (
    callLog.callStatus === "OUTGOING_INIT" ||
    callLog.callStatus === "OUTGOING_INIT_SUCCESS"
  )
    return "Outgoing";
  if (callLog.callStatus === "RINGING") return "Ringing";
  return "Call";
}

export const FloatingWhatsAppCallContainer = () => {
  const currentUserId = useSelector(selectCurrentUserId);
  const isMobile = useMediaQuery({ query: "(max-width: 735px)" });
  const history = useHistory();
  const [position, setPosition] = useState(getStoredPosition);
  const [minimized, setMinimized] = useState(getStoredMinimized);
  const [pendingOutgoing, setPendingOutgoing] =
    useState<InitiateWACallPayload | null>(null);
  const hadActiveOutgoingForPendingRef = useRef(false);

  const handleDrag = useCallback((_e: DraggableEvent, data: DraggableData) => {
    setPosition({ x: data.x, y: data.y });
  }, []);

  const handleDragStop = useCallback(
    (_e: DraggableEvent, data: DraggableData) => {
      setPosition({ x: data.x, y: data.y });
      try {
        sessionStorage.setItem(
          POSITION_STORAGE_KEY,
          JSON.stringify({ x: data.x, y: data.y }),
        );
      } catch {
        // ignore
      }
    },
    [],
  );

  const toggleMinimized = useCallback(() => {
    setMinimized((m) => {
      const next = !m;
      try {
        sessionStorage.setItem(MINIMIZED_STORAGE_KEY, String(next));
      } catch {
        // ignore
      }
      return next;
    });
  }, []);

  const { state: waCallLogsQuery } = useQueryWithStore(
    selectWACallLogsByRingAgentId(currentUserId ?? ""),
    loadWACallLogOfRingAgent(currentUserId ?? ""),
    [],
  );

  const list = useMemo(
    () => waCallLogsQuery?.list ?? [],
    [waCallLogsQuery?.list],
  );

  useEffect(() => {
    const handler = (payload: InitiateWACallPayload) => {
      hadActiveOutgoingForPendingRef.current = false;
      setPendingOutgoing(payload);
    };
    GlobalEventEmitter.addListener(INITATE_WA_CALL_EVENT, handler);
    return () => {
      GlobalEventEmitter.removeListener(INITATE_WA_CALL_EVENT, handler);
    };
  }, []);

  // —— Incoming: single active incoming call (in progress from customer, or ringing)
  const incomingRingingLogs = useMemo(() => {
    if (!currentUserId) return [];
    return list.filter((callLog) =>
      isWACallLogIncomingRinging(callLog, currentUserId, {
        withinMs: 60 * 1000,
      }),
    );
  }, [currentUserId, list]);

  const inProgressFromCustomer = useMemo(
    () =>
      list.filter(
        (c) => c.callStatus === "IN_PROGRESS" && c.startedSource === "CUSTOMER",
      ),
    [list],
  );

  const incomingCallLog = useMemo((): iWACallLog | null => {
    if (inProgressFromCustomer.length > 0) {
      const byStart = [...inProgressFromCustomer].sort(
        (a, b) => (b.startedAt ?? 0) - (a.startedAt ?? 0),
      );
      return byStart[0] ?? null;
    }
    return incomingRingingLogs[0] ?? null;
  }, [inProgressFromCustomer, incomingRingingLogs]);

  // —— Outgoing: pending (from event) or active outgoing from list
  const outgoingActiveFromList = useMemo(
    () => list.filter((c) => isOutgoingActive(c) || isOutgoingCall(c)),
    [list],
  );

  const outgoingCallLogFromList = useMemo((): iWACallLog | null => {
    if (pendingOutgoing) {
      const forConversation = list.filter(
        (c) => c.conversationId === pendingOutgoing.conversationId,
      );
      if (forConversation.length === 0) return null;
      const byStart = [...forConversation].sort(
        (a, b) => (b.startedAt ?? 0) - (a.startedAt ?? 0),
      );
      return byStart[0] ?? null;
    }
    if (outgoingActiveFromList.length === 0) return null;
    const byStart = [...outgoingActiveFromList].sort(
      (a, b) => (b.startedAt ?? 0) - (a.startedAt ?? 0),
    );
    return byStart[0] ?? null;
  }, [pendingOutgoing, list, outgoingActiveFromList]);

  // Clear pending only when the call we were showing for this pending has ended (not an old call in same conversation)
  useEffect(() => {
    if (!pendingOutgoing) {
      hadActiveOutgoingForPendingRef.current = false;
      return;
    }
    const current = outgoingCallLogFromList;
    if (current && isWACallLogActive(current)) {
      hadActiveOutgoingForPendingRef.current = true;
      return;
    }
    if (
      hadActiveOutgoingForPendingRef.current &&
      current &&
      !isWACallLogActive(current)
    ) {
      hadActiveOutgoingForPendingRef.current = false;
      setPendingOutgoing(null);
    }
  }, [pendingOutgoing, outgoingCallLogFromList]);

  // Decide mode: incoming takes priority, then outgoing
  const mode = useMemo((): "incoming" | "outgoing" | null => {
    if (incomingCallLog && isWACallLogActive(incomingCallLog))
      return "incoming";
    if (pendingOutgoing) return "outgoing";
    if (outgoingCallLogFromList && isWACallLogActive(outgoingCallLogFromList))
      return "outgoing";
    return null;
  }, [incomingCallLog, pendingOutgoing, outgoingCallLogFromList]);

  // Load full call log when we need it (incoming or outgoing with log from list)
  const displayedCallLogId =
    mode === "incoming"
      ? incomingCallLog?.id ?? null
      : mode === "outgoing"
      ? outgoingCallLogFromList?.id ?? null
      : null;

  const { state: displayedCallLog, isLoading: isLoadingDisplayed } =
    useQueryWithStore(
      selectWACallLogById(displayedCallLogId ?? ""),
      loadWACallLogById(displayedCallLogId ?? "")(),
      [displayedCallLogId],
      !displayedCallLogId,
    );

  useEffect(() => {
    if (incomingRingingLogs.length === 0) return;
    AudioNotificationsService.playAudio(AvailableAudios.INCOMING_CALL);
    const interval = setInterval(() => {
      AudioNotificationsService.playAudio(AvailableAudios.INCOMING_CALL);
    }, 27000);
    return () => {
      clearInterval(interval);
      AudioNotificationsService.stopAudio(AvailableAudios.INCOMING_CALL);
    };
  }, [incomingRingingLogs.length]);

  if (isMobile) return null;
  if (!currentUserId) return null;
  if (mode === null) return null;

  const browserId =
    (typeof window !== "undefined" && (window as any).browserSessionId) ?? "";

  const headerLabel =
    mode === "incoming"
      ? "WhatsApp Call – Incoming"
      : mode === "outgoing"
      ? displayedCallLog
        ? `WhatsApp Call – ${getCallStateLabel(displayedCallLog)}`
        : "WhatsApp Call – Outgoing"
      : "WhatsApp Call";

  const showLoading =
    mode === "outgoing" &&
    displayedCallLogId != null &&
    isLoadingDisplayed &&
    displayedCallLog == null;

  return (
    <Draggable
      position={position}
      onDrag={handleDrag}
      onStop={handleDragStop}
      handle=".floating-wa-call-drag-handle"
    >
      <div
        className="fixed right-10 bottom-10 z-50 w-[320px] rounded-lg border border-gray-300 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-800"
        style={{ cursor: "default" }}
      >
        <div
          className="floating-wa-call-drag-handle flex cursor-grab items-center justify-between gap-2 border-b border-gray-200 px-3 py-2 active:cursor-grabbing dark:border-gray-600"
          title="Drag to move"
        >
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {headerLabel}
          </span>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              toggleMinimized();
            }}
            className="flex h-7 w-7 items-center justify-center rounded text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-300"
            aria-label={minimized ? "Expand" : "Minimize"}
            title={minimized ? "Expand" : "Minimize"}
          >
            <i
              className={
                minimized
                  ? "ri-arrow-up-s-line text-lg"
                  : "ri-subtract-line text-lg"
              }
            />
          </button>
        </div>
        {!minimized && (
          <div className="p-2">
            {mode === "incoming" &&
              (isLoadingDisplayed && !displayedCallLog ? (
                <LoadingIndicatorWithoutSpin />
              ) : displayedCallLog ? (
                <>
                  {incomingRingingLogs.length > 1 && (
                    <div className="mb-2 flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 pb-2 text-xs text-gray-600 dark:border-gray-600 dark:text-gray-400">
                      <span>
                        You have {incomingRingingLogs.length} incoming calls
                      </span>
                      <button
                        type="button"
                        className="text-primary-600 hover:underline dark:text-primary-400"
                        onClick={() =>
                          history.push(
                            `/conversations/auto/${displayedCallLog.conversationId}`,
                          )
                        }
                      >
                        View conversation
                      </button>
                    </div>
                  )}
                  <WhatsappIncomingCallContainer callLog={displayedCallLog} />
                </>
              ) : null)}
            {mode === "outgoing" &&
              (showLoading ? (
                <LoadingIndicatorWithoutSpin />
              ) : (
                <WhatsappOutgoingCallContainer
                  callLog={
                    displayedCallLog && isWACallLogActive(displayedCallLog)
                      ? displayedCallLog
                      : undefined
                  }
                  conversationId={
                    pendingOutgoing?.conversationId ??
                    outgoingCallLogFromList?.conversationId ??
                    ""
                  }
                  messageId={pendingOutgoing?.messageId}
                  browserId={pendingOutgoing?.browserId ?? browserId}
                />
              ))}
          </div>
        )}
      </div>
    </Draggable>
  );
};
